import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { usStates, caProvinces, countryOptions } from './regions';
import {
  STRIPE_PUBLISH_KEY,
  STRIPE_PRICE_ID,
  initBackendVersion,
  validateUsername,
  getSetupIntentClientSecret,
  signup,
} from './api';
import type { FormErrors, ToastItem, SignupData } from './types';

declare const Stripe: (key: string) => any;
declare const grecaptcha: any;
declare const gtag: (...args: any[]) => void;

const RECAPTCHA_SITE_KEY = '6LdH-ssZAAAAAA_ZAkdzVFpCoejLyzkU-a39hw-A';

// Optional attribution question on step 3. Values are stored on the company.
const ACQUISITION_OPTIONS = [
  { value: 'referral', label: 'Referred by another company or colleague' },
  { value: 'search', label: 'Google / web search' },
  { value: 'review_site', label: 'Review site (Capterra, G2, ...)' },
  { value: 'association_event', label: 'Industry association or event' },
  { value: 'social', label: 'Social media' },
  { value: 'existing_user', label: 'Used Orna at another company' },
  { value: 'other', label: 'Other' },
];

const ACQUISITION_DETAIL_LABEL: Record<string, string> = {
  referral: 'Who referred you?',
  review_site: 'Which review site?',
  other: 'Tell us more',
};

const emptyErrors: FormErrors = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  newPassword1: '',
  newPassword2: '',
  companyName: '',
  companyAddress1: '',
  companyCity: '',
  companyState: '',
  companyZip: '',
  companyCountry: '',
  companyPhoneNumber: '',
  billingAddress1: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: '',
  card: '',
  terms: '',
};

export function SignupApp() {
  // Step navigation
  const [currentStep, setCurrentStep] = useState(1);
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right'>('left');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState<'leave' | 'enter' | null>(null);
  const [displayedStep, setDisplayedStep] = useState(1);

  // Step 1
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 2
  const [companyName, setCompanyName] = useState('');
  const [companyAddress1, setCompanyAddress1] = useState('');
  const [companyAddress2, setCompanyAddress2] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [companyZip, setCompanyZip] = useState('');
  const [companyCountry, setCompanyCountry] = useState('US');
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState('');
  const [hasDifferentBilling, setHasDifferentBilling] = useState(false);
  const [billingAddress1, setBillingAddress1] = useState('');
  const [billingAddress2, setBillingAddress2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('US');

  // Step 3
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(true);
  const [dispatchServices, setDispatchServices] = useState(false);
  const [acquisitionSource, setAcquisitionSource] = useState('');
  const [acquisitionSourceDetail, setAcquisitionSourceDetail] = useState('');

  // Errors
  const [errors, setErrors] = useState<FormErrors>({ ...emptyErrors });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonLoadingText, setButtonLoadingText] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdCounterRef = useRef(0);

  // Stripe
  const stripeReadyRef = useRef(false);
  const stripeRef = useRef<any>(null);
  const stripeCardElementRef = useRef<any>(null);
  const cardMountRef = useRef<HTMLDivElement>(null);

  // Config
  const [hideDispatchServices, setHideDispatchServices] = useState(false);

  // Computed values
  const companyRegionOptions = useMemo(
    () => (companyCountry === 'CA' ? caProvinces : usStates),
    [companyCountry],
  );
  const billingRegionOptions = useMemo(
    () => (billingCountry === 'CA' ? caProvinces : usStates),
    [billingCountry],
  );
  const companyRegionLabel = companyCountry === 'CA' ? 'Province' : 'State';
  const billingRegionLabel = billingCountry === 'CA' ? 'Province' : 'State';
  const companyZipLabel = companyCountry === 'CA' ? 'Postal Code' : 'Zip';
  const billingZipLabel = billingCountry === 'CA' ? 'Postal Code' : 'Zip';

  // Clear state when country changes
  useEffect(() => {
    setCompanyState('');
  }, [companyCountry]);
  useEffect(() => {
    setBillingState('');
  }, [billingCountry]);

  // Toast helper
  const showToast = useCallback((message: string, type: ToastItem['type'] = 'error') => {
    const id = ++toastIdCounterRef.current;
    const truncated = message.length > 125 ? message.substring(0, 122) + '...' : message;
    setToasts((prev) => [...prev, { id, message: truncated, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  function removeToast(id: number) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  // Google Analytics
  const updateGoogleAnalyticsPage = useCallback((stepNum: number) => {
    const pageUrls: Record<number, string> = {
      1: '/signup/page1',
      2: '/signup/page2',
      3: '/signup/page3',
    };
    const pageTitles: Record<number, string> = {
      1: 'Orna 21 Day Free Trial Signup Page 1',
      2: 'Orna 21 Day Free Trial Signup Page 2',
      3: 'Orna 21 Day Free Trial Signup Page 3',
    };
    const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
    dataLayer.push({
      event: 'pageview',
      page_location: pageUrls[stepNum] || '/signup/page' + stepNum,
      page_title: pageTitles[stepNum] || 'Orna Signup Page ' + stepNum,
    });
  }, []);

  // Init on mount
  useEffect(() => {
    initBackendVersion().catch(() => {
      showToast('Error getting backend version.', 'error');
    });

    const params = new URLSearchParams(window.location.search);
    if (params.get('referral') === 'criticalcm') {
      setHideDispatchServices(true);
    }

    updateGoogleAnalyticsPage(1);
  }, [showToast, updateGoogleAnalyticsPage]);

  // Analytics on step change
  useEffect(() => {
    if (currentStep > 1) {
      updateGoogleAnalyticsPage(currentStep);
    }
  }, [currentStep, updateGoogleAnalyticsPage]);

  // Stripe setup when step 3 becomes visible
  useEffect(() => {
    if (displayedStep !== 3) return;
    // Delay to allow transition to complete and DOM to be ready
    const timer = setTimeout(() => {
      if (!stripeReadyRef.current) {
        stripeReadyRef.current = true;

        stripeRef.current = Stripe(STRIPE_PUBLISH_KEY);
        const elements = stripeRef.current.elements();
        const style = {
          base: {
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': { color: '#aab7c4' },
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
          },
        };
        stripeCardElementRef.current = elements.create('card', { style });
        stripeCardElementRef.current.addEventListener('change', (event: any) => {
          setErrors((prev) => ({ ...prev, card: event.error ? event.error.message : '' }));
        });
      }
      // React recreates the container each time step 3 renders, so detach
      // from the old node (no-op on first visit) and mount into the new one.
      stripeCardElementRef.current.unmount();
      stripeCardElementRef.current.mount(cardMountRef.current);
    }, 400);
    return () => clearTimeout(timer);
  }, [displayedStep]);

  // Step transition
  function goToStep(n: number) {
    if (isTransitioning) return;
    const dir = n > currentStep ? 'left' : 'right';
    setTransitionDirection(dir);

    // Phase 1: leave animation
    setTransitionPhase('leave');
    setIsTransitioning(true);

    setTimeout(() => {
      // Swap displayed step
      setDisplayedStep(n);
      setCurrentStep(n);

      // Phase 2: enter animation
      setTransitionPhase('enter');

      setTimeout(() => {
        // Animation done
        setTransitionPhase(null);
        setIsTransitioning(false);
      }, 350);
    }, 350);
  }

  // Error helpers
  function clearStepErrors(step: number) {
    setErrors((prev) => {
      const next = { ...prev };
      if (step === 1) {
        next.firstName = '';
        next.lastName = '';
        next.email = '';
        next.username = '';
        next.newPassword1 = '';
        next.newPassword2 = '';
      } else if (step === 2) {
        next.companyName = '';
        next.companyAddress1 = '';
        next.companyCity = '';
        next.companyState = '';
        next.companyZip = '';
        next.companyCountry = '';
        next.companyPhoneNumber = '';
        next.billingAddress1 = '';
        next.billingCity = '';
        next.billingState = '';
        next.billingZip = '';
        next.billingCountry = '';
      } else if (step === 3) {
        next.card = '';
        next.terms = '';
      }
      return next;
    });
  }

  // Validation Step 1
  async function handleValidateStep1(e: React.FormEvent) {
    e.preventDefault();
    clearStepErrors(1);

    const newErrors = { ...emptyErrors };
    let valid = true;

    if (!firstName.trim()) { newErrors.firstName = 'First name required.'; valid = false; }
    if (!lastName.trim()) { newErrors.lastName = 'Last name required.'; valid = false; }
    if (!email.trim()) { newErrors.email = 'Email required.'; valid = false; }
    if (!username.trim()) {
      newErrors.username = 'Username required.'; valid = false;
    } else if (!/^[a-zA-Z0-9]+$/.test(username)) {
      newErrors.username = 'Username must be letters and numbers only.'; valid = false;
    } else if (username.length < 6) {
      newErrors.username = 'Username must be at least 6 characters.'; valid = false;
    } else if (username.length > 20) {
      newErrors.username = 'Username cannot be more than 20 characters.'; valid = false;
    }
    if (!newPassword1) { newErrors.newPassword1 = 'Password required.'; valid = false; }
    if (!newPassword2) { newErrors.newPassword2 = 'Password (repeat) required.'; valid = false; }

    if (!valid) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    if (newPassword1 !== newPassword2) {
      setErrors((prev) => ({ ...prev, newPassword2: 'Passwords do not match.' }));
      return;
    }
    if (newPassword1.length < 8) {
      setErrors((prev) => ({ ...prev, newPassword1: 'Password must be at least 8 characters.' }));
      return;
    }
    if (newPassword1.length > 50) {
      setErrors((prev) => ({ ...prev, newPassword1: 'Password cannot be more than 50 characters.' }));
      return;
    }
    if (
      !/[A-Z]/.test(newPassword1) ||
      !/[a-z]/.test(newPassword1) ||
      (!/\d/.test(newPassword1) && !/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(newPassword1))
    ) {
      setErrors((prev) => ({
        ...prev,
        newPassword1: 'Must include uppercase, lowercase, and a number or special character.',
      }));
      return;
    }

    // Async username validation
    setIsSubmitting(true);
    setButtonLoadingText('Validating...');

    try {
      const result = await validateUsername(username);
      setIsSubmitting(false);
      setButtonLoadingText('');
      if (result.validationError) {
        setErrors((prev) => ({ ...prev, username: result.validationError! }));
        return;
      }
      goToStep(2);
    } catch {
      setIsSubmitting(false);
      setButtonLoadingText('');
      showToast('Error validating username.', 'error');
    }
  }

  // Validation Step 2
  function handleValidateStep2(e: React.FormEvent) {
    e.preventDefault();
    clearStepErrors(2);

    const newErrors: Partial<FormErrors> = {};
    let valid = true;

    if (!companyName.trim()) { newErrors.companyName = 'Company/Agency name required.'; valid = false; }
    if (!companyAddress1.trim()) { newErrors.companyAddress1 = 'Address line 1 required.'; valid = false; }
    if (!companyCity.trim()) { newErrors.companyCity = 'City required.'; valid = false; }
    if (!companyState) { newErrors.companyState = companyRegionLabel + ' required.'; valid = false; }
    if (!companyZip.trim()) { newErrors.companyZip = companyZipLabel + ' required.'; valid = false; }
    if (!companyCountry) { newErrors.companyCountry = 'Country required.'; valid = false; }
    if (!companyPhoneNumber.trim()) { newErrors.companyPhoneNumber = 'Phone number required.'; valid = false; }

    if (hasDifferentBilling) {
      if (!billingAddress1.trim()) { newErrors.billingAddress1 = 'Billing address line 1 required.'; valid = false; }
      if (!billingCity.trim()) { newErrors.billingCity = 'Billing city required.'; valid = false; }
      if (!billingState) { newErrors.billingState = 'Billing ' + billingRegionLabel.toLowerCase() + ' required.'; valid = false; }
      if (!billingZip.trim()) { newErrors.billingZip = 'Billing ' + billingZipLabel.toLowerCase() + ' required.'; valid = false; }
      if (!billingCountry) { newErrors.billingCountry = 'Billing country required.'; valid = false; }
    }

    if (!valid) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }
    goToStep(3);
  }

  // Signup handler
  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    clearStepErrors(3);

    if (!agreedToTerms) {
      setErrors((prev) => ({ ...prev, terms: 'You must agree to the Terms & Conditions.' }));
      return;
    }

    setIsSubmitting(true);
    setButtonLoadingText('Signing Up...');

    try {
      // 1. Get setup intent
      const intentResult = await getSetupIntentClientSecret(false);
      if (!intentResult.clientSecret) {
        showToast('Unable to add card. Please contact Kerkton Support.', 'error');
        setIsSubmitting(false);
        setButtonLoadingText('');
        return;
      }

      // 2. Confirm card setup
      const cardResult = await stripeRef.current.confirmCardSetup(intentResult.clientSecret, {
        payment_method: { card: stripeCardElementRef.current },
      });
      if (cardResult.error) {
        showToast('Error validating card.', 'error');
        setIsSubmitting(false);
        setButtonLoadingText('');
        return;
      }
      const paymentMethodId = cardResult.setupIntent.payment_method;

      // 3. reCAPTCHA
      const recaptchaToken = await new Promise<string>((resolve) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action: 'validate_captcha' })
            .then((token: string) => resolve(token));
        });
      });

      // 4. Build signup data
      const signupData: SignupData = {
        personFirstName: firstName,
        personLastName: lastName,
        personEmail: email,
        username,
        newPassword1,
        newPassword2,
        companyName,
        companyAddress1,
        companyAddress2,
        companyCity,
        companyState,
        companyZip,
        companyCountry,
        companyPhoneNumber,
        agreedToTermsAndConditions: true,
        newsletter,
        dispatchServices,
        acquisitionSource,
        acquisitionSourceDetail: ['referral', 'review_site', 'other'].includes(acquisitionSource)
          ? acquisitionSourceDetail
          : '',
        priceId: STRIPE_PRICE_ID,
        paymentMethodId,
        recaptchaToken,
        billingAddress1: hasDifferentBilling ? billingAddress1 : companyAddress1,
        billingAddress2: hasDifferentBilling ? billingAddress2 : companyAddress2,
        billingCity: hasDifferentBilling ? billingCity : companyCity,
        billingState: hasDifferentBilling ? billingState : companyState,
        billingZip: hasDifferentBilling ? billingZip : companyZip,
        billingCountry: hasDifferentBilling ? billingCountry : companyCountry,
      };

      // 5. Submit
      await signup(signupData);

      setIsSubmitting(false);
      setButtonLoadingText('');

      gtag('event', 'conversion', {
        send_to: 'AW-10862870745/0iwRCLKXkfEbENmB6bso',
        value: 30.0,
        currency: 'USD',
      });

      setShowSuccessModal(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err: any) {
      setIsSubmitting(false);
      setButtonLoadingText('');

      if (
        err?.responseJSON?.errors?._embedded?.errors?.[0]?.message
      ) {
        showToast(err.responseJSON.errors._embedded.errors[0].message, 'error');
      } else {
        showToast('Error creating account.', 'error');
      }
    }
  }

  // Transition CSS class
  function getStepTransitionClass(): string {
    if (!transitionPhase) return '';
    if (transitionPhase === 'leave') {
      return transitionDirection === 'left'
        ? 'slide-left-leave-active slide-left-leave-to'
        : 'slide-right-leave-active slide-right-leave-to';
    }
    // enter: start with enter class, then remove it
    return transitionDirection === 'left'
      ? 'slide-left-enter-active slide-left-enter'
      : 'slide-right-enter-active slide-right-enter';
  }

  // Eye SVGs for password toggle
  const eyeOpen = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
  const eyeClosed = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  // Step content rendering
  function renderStep1() {
    return (
      <div key="step1">
        <h3 className="kst-section-title">Administrator Info</h3>
        <h5 className="kst-section-subtitle">
          For sales outside the USA and Canada, please email{' '}
          <a href="mailto:sales@kerkton.com">sales@kerkton.com</a>.
        </h5>
        <form className="kst-form" onSubmit={handleValidateStep1} noValidate>
          <div className="kst-input-group">
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder=" "
              className={errors.firstName ? 'has-error' : ''}
            />
            <label htmlFor="firstName">First Name</label>
            {errors.firstName && <div className="kst-field-error">{errors.firstName}</div>}
          </div>

          <div className="kst-input-group">
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder=" "
              className={errors.lastName ? 'has-error' : ''}
            />
            <label htmlFor="lastName">Last Name</label>
            {errors.lastName && <div className="kst-field-error">{errors.lastName}</div>}
          </div>

          <div className="kst-input-group">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder=" "
              className={errors.email ? 'has-error' : ''}
            />
            <label htmlFor="email">Email</label>
            {errors.email && <div className="kst-field-error">{errors.email}</div>}
          </div>

          <div className="kst-input-group">
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder=" "
              autoComplete="username"
              className={errors.username ? 'has-error' : ''}
            />
            <label htmlFor="username">Username</label>
            {errors.username ? (
              <div className="kst-field-error">{errors.username}</div>
            ) : (
              <div className="kst-field-hint">6&ndash;20 characters, letters and numbers only</div>
            )}
          </div>

          <div className="kst-input-group kst-input-group--password">
            <input
              type={showPassword ? 'text' : 'password'}
              id="newPassword1"
              value={newPassword1}
              onChange={(e) => setNewPassword1(e.target.value)}
              placeholder=" "
              autoComplete="new-password"
              className={errors.newPassword1 ? 'has-error' : ''}
            />
            <label htmlFor="newPassword1">Password</label>
            <button
              type="button"
              className="kst-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? eyeClosed : eyeOpen}
            </button>
            {errors.newPassword1 ? (
              <div className="kst-field-error">{errors.newPassword1}</div>
            ) : (
              <div className="kst-field-hint">
                8&ndash;50 characters, uppercase, lowercase, and a number or special character
              </div>
            )}
          </div>

          <div className="kst-input-group kst-input-group--password">
            <input
              type={showPassword ? 'text' : 'password'}
              id="newPassword2"
              value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              placeholder=" "
              autoComplete="new-password"
              className={errors.newPassword2 ? 'has-error' : ''}
            />
            <label htmlFor="newPassword2">Password (repeat)</label>
            <button
              type="button"
              className="kst-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? eyeClosed : eyeOpen}
            </button>
            {errors.newPassword2 && <div className="kst-field-error">{errors.newPassword2}</div>}
          </div>

          <button type="submit" className="kst-btn kst-btn-primary" disabled={isSubmitting}>
            {isSubmitting && <span className="kst-spinner" />}
            {isSubmitting ? buttonLoadingText : 'Next'}
          </button>
        </form>
      </div>
    );
  }

  function renderStep2() {
    return (
      <div key="step2">
        <h3 className="kst-section-title">Company/Agency Info</h3>
        <form className="kst-form" onSubmit={handleValidateStep2} noValidate>
          <div className="kst-input-group">
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder=" "
              className={errors.companyName ? 'has-error' : ''}
            />
            <label htmlFor="companyName">Company/Agency Name</label>
            {errors.companyName && <div className="kst-field-error">{errors.companyName}</div>}
          </div>

          <div className="kst-input-group">
            <input
              type="text"
              id="companyAddress1"
              value={companyAddress1}
              onChange={(e) => setCompanyAddress1(e.target.value)}
              placeholder=" "
              className={errors.companyAddress1 ? 'has-error' : ''}
            />
            <label htmlFor="companyAddress1">Address Line 1</label>
            {errors.companyAddress1 && <div className="kst-field-error">{errors.companyAddress1}</div>}
          </div>

          <div className="kst-input-group">
            <input
              type="text"
              id="companyAddress2"
              value={companyAddress2}
              onChange={(e) => setCompanyAddress2(e.target.value)}
              placeholder=" "
            />
            <label htmlFor="companyAddress2">Address Line 2</label>
          </div>

          <SearchableSelect
            value={companyCountry}
            options={countryOptions}
            placeholder="Select Country"
            label="Country"
            hasError={!!errors.companyCountry}
            onChange={setCompanyCountry}
          />
          {errors.companyCountry && (
            <div className="kst-field-error" style={{ marginTop: -20, marginBottom: 24 }}>
              {errors.companyCountry}
            </div>
          )}

          <div className="kst-input-group">
            <input
              type="text"
              id="companyCity"
              value={companyCity}
              onChange={(e) => setCompanyCity(e.target.value)}
              placeholder=" "
              className={errors.companyCity ? 'has-error' : ''}
            />
            <label htmlFor="companyCity">City</label>
            {errors.companyCity && <div className="kst-field-error">{errors.companyCity}</div>}
          </div>

          <SearchableSelect
            value={companyState}
            options={companyRegionOptions}
            placeholder={'Select ' + companyRegionLabel}
            label={companyRegionLabel}
            hasError={!!errors.companyState}
            onChange={setCompanyState}
          />
          {errors.companyState && (
            <div className="kst-field-error" style={{ marginTop: -20, marginBottom: 24 }}>
              {errors.companyState}
            </div>
          )}

          <div className="kst-input-group">
            <input
              type="text"
              id="companyZip"
              value={companyZip}
              onChange={(e) => setCompanyZip(e.target.value)}
              placeholder=" "
              className={errors.companyZip ? 'has-error' : ''}
            />
            <label htmlFor="companyZip">{companyZipLabel}</label>
            {errors.companyZip && <div className="kst-field-error">{errors.companyZip}</div>}
          </div>

          <div className="kst-input-group">
            <input
              type="text"
              id="companyPhoneNumber"
              value={companyPhoneNumber}
              onChange={(e) => setCompanyPhoneNumber(e.target.value)}
              placeholder=" "
              className={errors.companyPhoneNumber ? 'has-error' : ''}
            />
            <label htmlFor="companyPhoneNumber">Phone Number</label>
            {errors.companyPhoneNumber && (
              <div className="kst-field-error">{errors.companyPhoneNumber}</div>
            )}
          </div>

          <label className="kst-checkbox">
            <input
              type="checkbox"
              checked={hasDifferentBilling}
              onChange={(e) => setHasDifferentBilling(e.target.checked)}
            />
            <span>Different billing address</span>
          </label>

          {hasDifferentBilling && (
            <div>
              <div className="kst-input-group">
                <input
                  type="text"
                  id="billingAddress1"
                  value={billingAddress1}
                  onChange={(e) => setBillingAddress1(e.target.value)}
                  placeholder=" "
                  className={errors.billingAddress1 ? 'has-error' : ''}
                />
                <label htmlFor="billingAddress1">Billing Address Line 1</label>
                {errors.billingAddress1 && (
                  <div className="kst-field-error">{errors.billingAddress1}</div>
                )}
              </div>

              <div className="kst-input-group">
                <input
                  type="text"
                  id="billingAddress2"
                  value={billingAddress2}
                  onChange={(e) => setBillingAddress2(e.target.value)}
                  placeholder=" "
                />
                <label htmlFor="billingAddress2">Billing Address Line 2</label>
              </div>

              <SearchableSelect
                value={billingCountry}
                options={countryOptions}
                placeholder="Select Country"
                label="Billing Country"
                hasError={!!errors.billingCountry}
                onChange={setBillingCountry}
              />
              {errors.billingCountry && (
                <div className="kst-field-error" style={{ marginTop: -20, marginBottom: 24 }}>
                  {errors.billingCountry}
                </div>
              )}

              <div className="kst-input-group">
                <input
                  type="text"
                  id="billingCity"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  placeholder=" "
                  className={errors.billingCity ? 'has-error' : ''}
                />
                <label htmlFor="billingCity">Billing City</label>
                {errors.billingCity && <div className="kst-field-error">{errors.billingCity}</div>}
              </div>

              <SearchableSelect
                value={billingState}
                options={billingRegionOptions}
                placeholder={'Select ' + billingRegionLabel}
                label={'Billing ' + billingRegionLabel}
                hasError={!!errors.billingState}
                onChange={setBillingState}
              />
              {errors.billingState && (
                <div className="kst-field-error" style={{ marginTop: -20, marginBottom: 24 }}>
                  {errors.billingState}
                </div>
              )}

              <div className="kst-input-group">
                <input
                  type="text"
                  id="billingZip"
                  value={billingZip}
                  onChange={(e) => setBillingZip(e.target.value)}
                  placeholder=" "
                  className={errors.billingZip ? 'has-error' : ''}
                />
                <label htmlFor="billingZip">Billing {billingZipLabel}</label>
                {errors.billingZip && <div className="kst-field-error">{errors.billingZip}</div>}
              </div>
            </div>
          )}

          <div className="kst-btn-row">
            <button
              type="button"
              className="kst-btn kst-btn-secondary"
              onClick={() => goToStep(1)}
            >
              Previous
            </button>
            <button type="submit" className="kst-btn kst-btn-primary">
              Next
            </button>
          </div>
        </form>
      </div>
    );
  }

  function renderStep3() {
    return (
      <div key="step3">
        <h3 className="kst-section-title">Card Info and Terms</h3>
        <form className="kst-form" onSubmit={handleSignup} noValidate>
          <p className="kst-pricing">
            21-day free trial. Price after free trial: $10/user/month. 1000GB included/org.
            <br />
            See{' '}
            <a href="https://www.kerkton.com/pricing" target="_blank" rel="noreferrer">
              Pricing
            </a>{' '}
            page for full details.
          </p>

          <div className={`kst-stripe-card${errors.card ? ' has-error' : ''}`}>
            <div ref={cardMountRef} />
          </div>
          <div className="kst-card-errors">{errors.card}</div>

          <label className="kst-checkbox">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <span>
              I agree to the{' '}
              <a target="_blank" href="//kerkton.com/terms" rel="noreferrer">
                Terms &amp; Conditions
              </a>{' '}
              and{' '}
              <a target="_blank" href="//kerkton.com/privacy" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          </label>
          {errors.terms && (
            <div className="kst-field-error" style={{ marginTop: -12, marginBottom: 16 }}>
              {errors.terms}
            </div>
          )}

          <label className="kst-checkbox">
            <input
              type="checkbox"
              checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
            />
            <span>Sign up for newsletter. (optional)</span>
          </label>

          {!hideDispatchServices && (
            <label className="kst-checkbox">
              <input
                type="checkbox"
                checked={dispatchServices}
                onChange={(e) => setDispatchServices(e.target.checked)}
              />
              <span>Email me information about dispatch services. (optional)</span>
            </label>
          )}

          <div style={{ marginTop: 16 }}>
            <SearchableSelect
              value={acquisitionSource}
              options={ACQUISITION_OPTIONS}
              placeholder="Select an option"
              label="How did you hear about us? (optional)"
              hasError={false}
              clearable
              onChange={setAcquisitionSource}
            />
            {ACQUISITION_DETAIL_LABEL[acquisitionSource] && (
              <div className="kst-input-group">
                <input
                  type="text"
                  id="acquisitionSourceDetail"
                  value={acquisitionSourceDetail}
                  onChange={(e) => setAcquisitionSourceDetail(e.target.value)}
                  placeholder=" "
                  maxLength={255}
                />
                <label htmlFor="acquisitionSourceDetail">
                  {ACQUISITION_DETAIL_LABEL[acquisitionSource]} (optional)
                </label>
              </div>
            )}
          </div>

          <div className="kst-btn-row">
            <button
              type="button"
              className="kst-btn kst-btn-secondary"
              disabled={isSubmitting}
              onClick={() => goToStep(2)}
            >
              Previous
            </button>
            <button type="submit" className="kst-btn kst-btn-primary" disabled={isSubmitting}>
              {isSubmitting && <span className="kst-spinner" />}
              {isSubmitting ? buttonLoadingText : 'Sign Up'}
            </button>
          </div>

          <div className="kst-footnote">
            *Discounted pricing available for non-profits. Please email sales@kerkton.com to apply
            discounted pricing after signup. 1000GB of data storage is included but $12/1000GB/mo
            after.
          </div>
        </form>
      </div>
    );
  }

  function renderCurrentStep() {
    switch (displayedStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  }

  return (
    <>
      {/* Navbar */}
      <nav className="kst-navbar">
        <a href="#">
          <img src="/signup/img/kst_logo_white_small.png" alt="Kerkton" loading="lazy" />
        </a>
      </nav>

      <div className="kst-signup-container">
        {/* Progress Steps */}
        <div className="kst-progress-steps">
          <div className={`kst-step${currentStep === 1 ? ' active' : ''}${currentStep > 1 ? ' completed' : ''}`}>
            <div className="kst-step-circle">
              {currentStep > 1 ? '\u2713' : '1'}
            </div>
            <div className="kst-step-label">Admin</div>
          </div>
          <div className={`kst-step-line${currentStep > 1 ? ' completed' : ''}`} />
          <div className={`kst-step${currentStep === 2 ? ' active' : ''}${currentStep > 2 ? ' completed' : ''}`}>
            <div className="kst-step-circle">
              {currentStep > 2 ? '\u2713' : '2'}
            </div>
            <div className="kst-step-label">Company</div>
          </div>
          <div className={`kst-step-line${currentStep > 2 ? ' completed' : ''}`} />
          <div className={`kst-step${currentStep === 3 ? ' active' : ''}`}>
            <div className="kst-step-circle">3</div>
            <div className="kst-step-label">Review</div>
          </div>
        </div>

        {/* Step Content with transition */}
        <div className={getStepTransitionClass()} style={{ transition: 'all 0.35s ease' }}>
          {renderCurrentStep()}
        </div>
      </div>

      {/* Footer */}
      <div className="kst-footer">
        <a target="_blank" href="//kerkton.com/terms" rel="noreferrer">
          Terms &amp; Conditions
        </a>{' '}
        |{' '}
        <a target="_blank" href="//kerkton.com/privacy" rel="noreferrer">
          Privacy Policy
        </a>
      </div>

      {/* Toast Notifications */}
      <div className="kst-toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`kst-toast kst-toast-${toast.type}`}>
            {toast.message}
            <button className="kst-toast-close" onClick={() => removeToast(toast.id)}>
              &times;
            </button>
          </div>
        ))}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="kst-modal-overlay">
          <div className="kst-modal">
            <div className="kst-modal-icon">
              <svg viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3>Account Created!</h3>
            <p>Your account has been created. Redirecting to login...</p>
          </div>
        </div>
      )}
    </>
  );
}
